import React from 'react'
import {
  MenuItem,
  Select,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableBody,
  Box,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { sortBy } from 'lodash'
import {
  UserWithRoleFragment,
  UserRole,
  useUsersQuery,
  useUpdateUserMutation,
} from '../../generated/graphql'
import { useNotifications } from '../../notifications/use-notifications'
import { useProfile } from '../profile/ProfileProvider'
import { Loader } from '../common/Loader'

export function UsersPage(): React.ReactElement {
  const { id: currentUserId } = useProfile()
  const { data } = useUsersQuery()
  const [updateMutation] = useUpdateUserMutation()
  const { t } = useTranslation()
  const { enqueueInfo, enqueueSuccess, enqueueError, closeSnackbar } =
    useNotifications()

  if (!data) {
    return <Loader />
  }

  const handleRoleChange =
    (user: UserWithRoleFragment) =>
    async (event: React.ChangeEvent<{ value: unknown }>) => {
      const role = event.target.value as UserRole
      const infoKey = enqueueInfo(
        t('users.updateInfo', { name: user.displayName, role }),
      )

      try {
        await updateMutation({
          variables: {
            id: user.id,
            input: { role },
          },
        })

        enqueueSuccess(
          t('users.updateSuccess', {
            name: user.displayName,
            role: t(`userRole.${role}`),
          }),
        )
      } catch (error) {
        enqueueError(
          t('users.updateError', {
            name: user.displayName,
            error: (error as Error).message,
          }),
        )
      }

      closeSnackbar(infoKey)
    }

  const sortedUsers = sortBy(data.users, (user) =>
    user.displayName.toLocaleLowerCase(),
  )

  return (
    <Box>
      <Helmet title={t('users.title')} />

      <Typography variant="h3">{t('users.title')}</Typography>

      <Box my={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="90%" data-testid="users-table-header">
                  {t('users.tableHeaderName')}
                </TableCell>
                <TableCell>{t('users.tableHeaderRole')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.displayName}</TableCell>
                  <TableCell>
                    <Select
                      value={user.role}
                      onChange={handleRoleChange(user)}
                      disabled={user.id === currentUserId}
                      data-cy="change-user-role"
                    >
                      {Object.values(UserRole).map((role) => (
                        <MenuItem key={role} value={role}>
                          {t(`userRole.${role}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}
